import React, { useState, useRef } from 'react';
import { Delete as DeleteIcon, Plus, Search, Upload, Loading } from '@icon-park/react';
import { uploadFile } from '../../api/upload';
import { queryReportByCode, getReportDownloadUrl } from '../../api/inspection';
import './styles/InspectionReport.css';
import DocumentPreviewModal from '../common/DocumentPreviewModal';
import { certificateAttachment, inspectionFiles } from '../../utils/certificate';

function InspectionReport({ data, onUpdate, labelId, giCertificate = false }) {
  const defaultAttachments = [
    { enabled: true, name: '标准文本', file: null, sourceType: 'upload' },
    { enabled: true, name: '检验报告', file: null, sourceType: 'upload' }
  ];

  const attachments = data.attachments || defaultAttachments;
  const latest = useRef({ data, attachments, onUpdate });
  latest.current = { data, attachments, onUpdate };
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [queryingIndex, setQueryingIndex] = useState(null);
  const [queryError, setQueryError] = useState(null);
  const [queryReportCodes, setQueryReportCodes] = useState({}); // 每个附件独立的查询编号

  // 切换数据源类型：查询或上传
  const handleSourceTypeChange = (index, sourceType) => {
    const newAttachments = latest.current.attachments.map(att => ({ ...att }));
    newAttachments[index].sourceType = sourceType;
    // 切换时清空文件
    newAttachments[index].file = null;
    latest.current.onUpdate({ ...latest.current.data, attachments: newAttachments });
    setQueryError(null);
    setUploadError(null);
  };

  // 查询报告
  const handleQueryReport = async (index) => {
    const reportCode = queryReportCodes[index] || '';
    if (!reportCode.trim()) {
      setQueryError('请输入报告编号');
      return;
    }

    setQueryingIndex(index);
    setQueryError(null);

    try {
      const report = await queryReportByCode(reportCode.trim());
      
      if (!report || !report.filePath) {
        setQueryError('该报告未找到文件');
        return;
      }

      // 更新附件数据
      const newAttachments = latest.current.attachments.map(att => ({ ...att }));
      newAttachments[index].file = {
        name: report.name || `报告_${report.reportCode}`,
        size: null,
        type: 'application/pdf',
        url: getReportDownloadUrl(report.reportCode, true),
        reportId: report.id,
        reportCode: report.reportCode,
        sourceType: 'query'
      };
      // 如果没有自定义名称，使用报告名称
      if (!newAttachments[index].name || newAttachments[index].name === '新附件') {
        newAttachments[index].name = report.name || `报告_${report.reportCode}`;
      }
      latest.current.onUpdate({ ...latest.current.data, attachments: newAttachments });
      // 清空该附件的查询输入
      setQueryReportCodes({ ...queryReportCodes, [index]: '' });
    } catch (error) {
      console.error('查询报告失败:', error);
      setQueryError(error.message || '查询失败，请检查报告编号是否正确');
    } finally {
      setQueryingIndex(null);
    }
  };

  // 上传文件
  const handleFileUpload = async (index, e) => {
    const input = e.target;
    const file = input.files[0];
    input.value = '';
    if (!file) return;

    // 验证文件大小（最大10MB）
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setUploadError(`文件大小超过限制（最大${(maxSize / 1024 / 1024).toFixed(0)}MB）`);
      return;
    }

    // 验证文件类型
    const allowedTypes = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'];
    const fileName = file.name.toLowerCase();
    const isValidType = allowedTypes.some(ext => fileName.endsWith(ext));
    if (!isValidType) {
      setUploadError(`不支持的文件格式，请上传：${allowedTypes.join(', ')}`);
      return;
    }

    setUploadingIndex(index);
    setUploadError(null);

    try {
      // 调用文件上传API
      const fileUrl = await uploadFile(file, {
        fileType: 'attachment',
        labelId: labelId || undefined,
        onProgress: (progress) => {
          console.log(`上传进度: ${progress}%`);
        }
      });

      // 更新附件数据，包含文件URL
      const newAttachments = latest.current.attachments.map(att => ({ ...att }));
      newAttachments[index].file = {
        name: file.name,
        size: file.size,
        type: file.type,
        url: fileUrl,
        sourceType: 'upload'
      };
      latest.current.onUpdate({ ...latest.current.data, attachments: newAttachments });
    } catch (error) {
      console.error('文件上传失败:', error);
      setUploadError(error.message || '文件上传失败，请稍后重试');
      input.value = '';
    } finally {
      setUploadingIndex(null);
    }
  };

  const handleDeleteFile = (index) => {
    const newAttachments = latest.current.attachments.map(att => ({ ...att }));
    newAttachments[index].file = null;
    latest.current.onUpdate({ ...latest.current.data, attachments: newAttachments });
  };

  const handleNameChange = (index, name) => {
    const newAttachments = latest.current.attachments.map(att => ({ ...att }));
    newAttachments[index].name = name;
    latest.current.onUpdate({ ...latest.current.data, attachments: newAttachments });
  };

  const handleAddAttachment = () => {
    const newAttachments = latest.current.attachments.map(att => ({ ...att }));
    newAttachments.push({ enabled: true, name: '新附件', file: null, sourceType: 'upload' });
    latest.current.onUpdate({ ...latest.current.data, attachments: newAttachments });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="inspection-report-container">
      <div className="template-header-simple">
        {/* 展示样式选择 */}
        <div className="style-section">
          <span className="style-section-label">展示样式</span>
          <div className="style-radio-group">
            <label className={`style-radio-item ${!data.buttonStyle ? 'active' : ''}`}>
              <input
                type="radio"
                name="inspect-style"
                checked={!data.buttonStyle}
                onChange={() => onUpdate({ ...data, buttonStyle: null })}
              />
              <span>列表</span>
            </label>
            <label className={`style-radio-item ${data.buttonStyle ? 'active' : ''}`}>
              <input
                type="radio"
                name="inspect-style"
                checked={!!data.buttonStyle}
                onChange={() => onUpdate({ ...data, buttonStyle: { ...(data.buttonStyle || { variant: 'gold' }) } })}
              />
              <span>按钮</span>
            </label>
          </div>
        </div>

        {/* 列表模式 - 边框颜色 */}
        {!data.buttonStyle && (
          <div className="style-section">
            <span className="style-section-label">边框颜色</span>
            <input
              type="color"
              value={data.borderColor || '#e8eaed'}
              onChange={(e) => onUpdate({ ...data, borderColor: e.target.value })}
              className="color-input-inline"
            />
          </div>
        )}

        {/* 按钮模式 - 按钮配置 */}
        {data.buttonStyle && (
          <div className="button-config-section">
            <div className="config-row">
              <span className="config-label">按钮风格</span>
              <select
                value={(data.buttonStyle && data.buttonStyle.variant) || 'gold'}
                onChange={(e) => onUpdate({ ...data, buttonStyle: { ...(data.buttonStyle || {}), variant: e.target.value } })}
                className="config-select"
              >
                <option value="gold">金色渐变</option>
                <option value="blue">蓝色渐变</option>
                <option value="custom">自定义</option>
              </select>
            </div>
            <div className="config-row">
              <div className="config-item">
                <span className="config-label">起始色</span>
                <input
                  type="color"
                  value={(data.buttonStyle && data.buttonStyle.bgStart) || '#d79a3d'}
                  onChange={(e) => onUpdate({ ...data, buttonStyle: { ...(data.buttonStyle || {}), bgStart: e.target.value } })}
                  className="config-color"
                />
              </div>
              <div className="config-item">
                <span className="config-label">结束色</span>
                <input
                  type="color"
                  value={(data.buttonStyle && data.buttonStyle.bgEnd) || '#b7791f'}
                  onChange={(e) => onUpdate({ ...data, buttonStyle: { ...(data.buttonStyle || {}), bgEnd: e.target.value } })}
                  className="config-color"
                />
              </div>
              <div className="config-item">
                <span className="config-label">文字色</span>
                <input
                  type="color"
                  value={(data.buttonStyle && data.buttonStyle.textColor) || '#ffffff'}
                  onChange={(e) => onUpdate({ ...data, buttonStyle: { ...(data.buttonStyle || {}), textColor: e.target.value } })}
                  className="config-color"
                />
              </div>
              <div className="config-item">
                <span className="config-label">圆角</span>
                <input
                  type="number"
                  min="0"
                  max="30"
                  value={(data.buttonStyle && data.buttonStyle.radius) || 10}
                  onChange={(e) => onUpdate({ ...data, buttonStyle: { ...(data.buttonStyle || {}), radius: Number(e.target.value) } })}
                  className="config-number"
                />
              </div>
              <div className="config-item config-item-checkbox">
                <span className="config-label">阴影</span>
                <input
                  type="checkbox"
                  checked={!data.buttonStyle || data.buttonStyle.shadow !== false}
                  onChange={(e) => onUpdate({ ...data, buttonStyle: { ...(data.buttonStyle || {}), shadow: e.target.checked } })}
                  className="config-checkbox-input"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {giCertificate && <div className="inspection-certificate-tip"><strong>GI 检测证书</strong><p>上传 PDF 或 Word（.docx）证书，用户点击标签顶部“GI 检测证书”即可直接预览。单个文件最大 10MB。</p>
        <label>顶部证书按钮对应附件<select aria-label="顶部证书按钮对应附件" value={certificateAttachment(data)?.index ?? ''} onChange={e => onUpdate({ ...data, giCertificateIndex: Number(e.target.value) })}>
          {!inspectionFiles(data).length && <option value="">请先上传证书文件</option>}
          {inspectionFiles(data).map(att => <option key={att.index} value={att.index}>{att.name || att.file.name}</option>)}
        </select></label>
      </div>}
      <h3 className="section-title">附件</h3>
      <p className="inspection-file-hint">PDF、Word（.docx）支持在线预览；旧版 .doc 请另存为 .docx 或 PDF 后上传。</p>

      <div className="attachments-table">
        <div className="attachments-header">
          <div className="col-checkbox-att"></div>
          <div className="col-name-att">名称</div>
          <div className="col-source-att">数据源</div>
          <div className="col-file-att">文件</div>
          <div className="col-action-att">操作</div>
        </div>

        {attachments.map((att, index) => {
          const sourceType = att.sourceType || 'upload';
          const isUploading = uploadingIndex === index;
          const isQuerying = queryingIndex === index;
          
          return (
            <div key={index} className="attachments-row">
              <div className="col-checkbox-att">
                <input
                  type="checkbox"
                  checked={att.enabled}
                  onChange={(e) => {
                    const newAttachments = latest.current.attachments.map(att => ({ ...att }));
                    newAttachments[index].enabled = e.target.checked;
                    latest.current.onUpdate({ ...latest.current.data, attachments: newAttachments });
                  }}
                  className="att-checkbox"
                />
              </div>
              <div className="col-name-att">
                <input
                  type="text"
                  className="att-name-input"
                  value={att.name}
                  onChange={(e) => handleNameChange(index, e.target.value)}
                  placeholder="附件名称"
                />
              </div>
              <div className="col-source-att">
                <div className="source-type-selector">
                  <label className={`source-type-option ${sourceType === 'query' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name={`source-type-${index}`}
                      checked={sourceType === 'query'}
                      onChange={() => handleSourceTypeChange(index, 'query')}
                      disabled={isUploading || isQuerying}
                    />
                    <span>查询报告</span>
                  </label>
                  <label className={`source-type-option ${sourceType === 'upload' ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name={`source-type-${index}`}
                      checked={sourceType === 'upload'}
                      onChange={() => handleSourceTypeChange(index, 'upload')}
                      disabled={isUploading || isQuerying}
                    />
                    <span>自定义上传</span>
                  </label>
                </div>
              </div>
              <div className="col-file-att">
                {sourceType === 'query' ? (
                  <div className="query-section">
                    {att.file ? (
                      <div className="file-info">
                        <span className="file-name">{att.file.name}</span>
                        {att.file.reportCode && (
                          <span className="file-meta">报告编号: {att.file.reportCode}</span>
                        )}
                        <span className="file-source-badge">来自系统</span>
                      </div>
                    ) : (
                      <div className="query-input-group">
                        <input
                          type="text"
                          className="query-report-input"
                          value={queryReportCodes[index] || ''}
                          onChange={(e) => setQueryReportCodes({ ...queryReportCodes, [index]: e.target.value })}
                          placeholder="请输入报告编号"
                          disabled={isQuerying}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && !isQuerying) {
                              handleQueryReport(index);
                            }
                          }}
                        />
                        <button
                          className="query-report-btn"
                          onClick={() => handleQueryReport(index)}
                          disabled={isQuerying || !(queryReportCodes[index] || '').trim()}
                        >
                          {isQuerying ? (
                            <>
                              <Loading theme="outline" size={14} fill="#333" strokeWidth={3} />
                              查询中...
                            </>
                          ) : (
                            <>
                              <Search theme="outline" size={14} fill="#333" strokeWidth={3} />
                              查询
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="upload-section">
                    {att.file ? (
                      <div className="file-info">
                        <span className="file-name">{att.file.name}</span>
                        {att.file.size && (
                          <span className="file-size">{formatFileSize(att.file.size)}</span>
                        )}
                        <span className="file-source-badge">自定义上传</span>
                      </div>
                    ) : (
                      <span className="no-file-text">未选文件.txt</span>
                    )}
                  </div>
                )}
              </div>
              <div className="col-action-att">
                {att.file?.url && <button type="button" className="certificate-preview-action" onClick={() => setPreviewAttachment(att)}>预览文件</button>}
                {sourceType === 'query' ? (
                  att.file && (
                    <button
                      className="delete-file-btn"
                      onClick={() => handleDeleteFile(index)}
                      disabled={isQuerying}
                    >
                      <DeleteIcon theme="outline" size={14} fill="#333" strokeWidth={3} /> 移除
                    </button>
                  )
                ) : (
                  <>
                    <input
                      type="file"
                      id={`file-upload-${index}`}
                      onChange={(e) => handleFileUpload(index, e)}
                      disabled={uploadingIndex !== null || queryingIndex !== null}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                      style={{ display: 'none' }}
                    />
                    <label
                      htmlFor={`file-upload-${index}`}
                      className={`upload-file-btn ${isUploading ? 'uploading' : ''}`}
                      style={{ opacity: isUploading ? 0.6 : 1, cursor: isUploading ? 'not-allowed' : 'pointer' }}
                    >
                      {isUploading ? (
                        <>
                          <Loading theme="outline" size={14} fill="#333" strokeWidth={3} />
                          上传中...
                        </>
                      ) : (
                        <>
                          <Upload theme="outline" size={14} fill="#333" strokeWidth={3} />
                          {att.file ? '重新上传' : '上传文件'}
                        </>
                      )}
                    </label>
                    {att.file && (
                      <button
                        className="delete-file-btn"
                        onClick={() => handleDeleteFile(index)}
                        disabled={uploadingIndex !== null || queryingIndex !== null}
                      >
                        <DeleteIcon theme="outline" size={14} fill="#333" strokeWidth={3} /> 删除附件
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <button className="add-attachment-btn" disabled={uploadingIndex !== null || queryingIndex !== null} onClick={handleAddAttachment}>
        <span className="add-icon"><Plus theme="outline" size={18} fill="#333" strokeWidth={3} /></span>
        添加附件
      </button>

      {previewAttachment && <DocumentPreviewModal attachment={previewAttachment} onClose={() => setPreviewAttachment(null)} />}
      {(uploadError || queryError) && (
        <div className="error-message">
          {uploadError || queryError}
        </div>
      )}
    </div>
  );
}

export default InspectionReport;
